import { useMemo, useState } from "react"
type SectionId = "strategy" | "audience" | "targeting" | "budget"
import {
  ChevronDownIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/outline"
import WorldMapSelector from "./WorldMapSelector"
import type {
  CampaignData,
  CampaignType,
  OptimizationGoal,
  AttributionModel,
  AttributionWindow,
  AdsetStrategy,
  TargetingGeography,
  TargetingGender,
  Placements,
  BudgetOptimization,
  BiddingStrategy,
  Platform,
} from "../types"

interface CampaignSetupProps {
  data: CampaignData
  onChange: (data: CampaignData) => void
  onNext: () => void
  errorFields?: string[]
}

const CAMPAIGN_TYPES: CampaignType[] = [
  "Cold Prospecting DR",
  "Warm Prospecting DR",
  "Lukewarm Prospecting DR",
  "Retention DR",
  "Demand Generation - Engagement",
  "Demand Generation - Traffic",
  "Demand Generation - Awareness",
]

function getOptimizationGoals(campaignType: CampaignType): OptimizationGoal[] {
  switch (campaignType) {
    case "Cold Prospecting DR":
    case "Warm Prospecting DR":
    case "Lukewarm Prospecting DR":
    case "Retention DR":
      return ["Max Conversions", "Max Conversion Value", "Landing Page Views"]
    case "Demand Generation - Awareness":
      return ["Max Conversions", "Max Conversion Value", "Landing Page Views"]
    case "Demand Generation - Engagement":
      return ["Post Engagements"]
    case "Demand Generation - Traffic":
      return ["Web Visits", "Profile Visits"]
    default:
      return []
  }
}

function fieldHasValue(field: string, data: CampaignData): boolean {
  switch (field) {
    case "campaignType":
      return !!data.campaignType
    case "optimizationGoal":
      return !!data.optimizationGoal
    case "attributionModel":
      return !!data.attributionModel
    case "attributionWindow":
      return !!data.attributionWindow
    case "adsetStrategy":
      return !!data.adsetStrategy
    case "targetingGeography":
      return !!data.targetingGeography
    case "targetingGender":
      return !!data.targetingGender
    case "placements":
      return !!data.placements
    case "budgetOptimization":
      return !!data.budgetOptimization
    case "biddingStrategy":
      return !!data.biddingStrategy
    case "budgetAmount":
      return data.budgetAmount > 0
    case "platforms":
      return data.platforms.length > 0
    default:
      return true
  }
}

function isEngagementCampaignType(campaignType: CampaignType): boolean {
  return campaignType === "Demand Generation - Engagement"
}

function isDRCampaignType(campaignType: CampaignType): boolean {
  return (
    campaignType === "Cold Prospecting DR" ||
    campaignType === "Warm Prospecting DR" ||
    campaignType === "Lukewarm Prospecting DR" ||
    campaignType === "Retention DR"
  )
}

const ATTRIBUTION_MODELS: AttributionModel[] = ["Standard", "Incremental"]
const ATTRIBUTION_WINDOWS: AttributionWindow[] = [
  "7d Click",
  "7d Click + 1d View",
  "7d Click + 1d Engaged View",
  "1d Click",
]
const ADSET_STRATEGIES: AdsetStrategy[] = [
  "Single Adset",
  "Split by Creative",
  "Product Groups",
]
const TARGETING_GENDERS: TargetingGender[] = ["All", "Male", "Female"]
const PLACEMENTS_OPTIONS: Placements[] = [
  "All Placements",
  "Advantage+",
  "Feed/Stories/Reels only",
  "Feed only",
  "Stories/Reels only",
]
const BUDGET_OPTIMIZATIONS: BudgetOptimization[] = [
  "Campaign (CBO)",
  "Adset (ABO)",
]
const ALL_PLATFORMS: Platform[] = ["Facebook", "Instagram", "Audience Network"]
const AGE_GROUPS = [
  { label: "18-24", min: 18, max: 24 },
  { label: "25-34", min: 25, max: 34 },
  { label: "35-44", min: 35, max: 44 },
  { label: "45-54", min: 45, max: 54 },
  { label: "55-64", min: 55, max: 64 },
  { label: "65+", min: 65, max: 65 },
]

function generateCampaignTitle(data: CampaignData): string {
  const parts: string[] = []
  if (data.campaignType) parts.push(data.campaignType.replace(/\s+/g, ""))
  if (data.optimizationGoal) parts.push(`Opt:${data.optimizationGoal}`)
  if (data.attributionModel) {
    parts.push(`Attr:${data.attributionModel.replace(/\s+/g, "")}`)
  }
  if (data.attributionWindow) {
    parts.push(`Win:${data.attributionWindow.replace(/\s+/g, "")}`)
  }
  if (data.targetingGeography) {
    parts.push(`Geo:${data.targetingGeography.replace(/\s+/g, "")}`)
  }
  if (data.targetingGender && data.targetingGender !== "All") {
    parts.push(`Gen:${data.targetingGender}`)
  }
  parts.push(`Age:${data.ageMin}-${data.ageMax}`)
  if (data.placements) parts.push(`Plc:${data.placements.replace(/\s+/g, "")}`)
  return parts.join("_")
}

// ── Reusable components ──

function SelectField({
  label,
  value,
  options,
  onChange,
  error,
  disabled,
}: {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  error?: boolean
  disabled?: boolean
}) {
  return (
    <div>
      <label
        className={`block text-xs font-medium mb-1 uppercase tracking-wider ${
          error ? "text-error" : "text-basic-light/50"
        }`}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className={`select select-bordered select-sm w-full bg-system-gray-900 text-basic-white text-sm py-0 px-2 min-h-0 h-8 [&>option]:bg-system-gray-900 [&>option]:text-basic-white ${
          error ? "border-error" : ""
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <option value="" disabled>
          Select...
        </option>
        {options.map(opt => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  )
}

function CollapsibleSection({
  title,
  subtitle,
  children,
  isOpen,
  onToggle,
  complete,
  error,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
  isOpen: boolean
  onToggle: () => void
  complete?: boolean
  error?: boolean
}) {
  const badgeClass = complete
    ? "bg-green-600/20 text-green-400"
    : error
      ? "bg-error/20 text-error"
      : "bg-yellow-600/20 text-yellow-400"
  const borderClass =
    error && !complete ? "border-error/50" : "border-system-gray-700/30"

  return (
    <div className={`border rounded-xl overflow-hidden ${borderClass}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-system-gray-900/30 hover:bg-system-gray-900/50 transition-colors"
      >
        <div className="text-left">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-basic-white">{title}</h3>
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeClass}`}
            >
              {complete ? "Done" : "Required"}
            </span>
          </div>
          {subtitle && (
            <p className="text-xs text-basic-light/40 mt-0.5">{subtitle}</p>
          )}
        </div>
        <ChevronDownIcon
          className={`w-4 h-4 text-basic-light/40 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="px-4 py-3 border-t border-system-gray-700/20">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main component ──

export default function CampaignSetup({
  data,
  onChange,
  onNext,
  errorFields = [],
}: CampaignSetupProps) {
  const [attempted, setAttempted] = useState(false)
  const hasError = (field: string) =>
    errorFields.includes(field) || (attempted && !fieldHasValue(field, data))

  const initialSection = (): SectionId => {
    if (errorFields.length === 0) return "strategy"
    const strategyFields = [
      "campaignType",
      "optimizationGoal",
      "attributionModel",
      "attributionWindow",
    ]
    const budgetFields = [
      "budgetOptimization",
      "biddingStrategy",
      "budgetAmount",
    ]
    const targetingFields = [
      "targetingGeography",
      "targetingGender",
      "placements",
      "ageMin",
      "ageMax",
    ]
    if (errorFields.some(f => strategyFields.includes(f))) return "strategy"
    if (errorFields.some(f => targetingFields.includes(f))) return "targeting"
    if (errorFields.some(f => budgetFields.includes(f))) return "budget"
    return "strategy"
  }

  const [openSection, setOpenSection] = useState<SectionId>(initialSection)

  const toggleSection = (id: SectionId) => {
    setOpenSection(prev => (prev === id ? prev : id))
  }

  const campaignTitle = useMemo(() => generateCampaignTitle(data), [data])

  const optimizationGoals = useMemo(
    () => getOptimizationGoals(data.campaignType),
    [data.campaignType],
  )

  const isEngagement = isEngagementCampaignType(data.campaignType)

  const biddingStrategies = useMemo((): BiddingStrategy[] => {
    const base: BiddingStrategy[] = ["Max Volume", "Cost Cap"]
    if (isDRCampaignType(data.campaignType)) {
      base.push("ROAS Target")
    }
    return base
  }, [data.campaignType])

  const handleChange = <K extends keyof CampaignData>(
    field: K,
    value: CampaignData[K],
  ) => {
    const updated = { ...data, [field]: value }

    if (field === "campaignType") {
      const newGoals = getOptimizationGoals(value as CampaignType)
      if (newGoals.length === 1) {
        updated.optimizationGoal = newGoals[0] as OptimizationGoal
      } else if (!newGoals.includes(data.optimizationGoal)) {
        updated.optimizationGoal = "" as OptimizationGoal
      }
      // Engagement campaigns: lock attribution to 1d Click / Standard
      if (isEngagementCampaignType(value as CampaignType)) {
        updated.attributionWindow = "1d Click" as AttributionWindow
        updated.attributionModel = "Standard" as AttributionModel
      }
      if (
        !isDRCampaignType(value as CampaignType) &&
        data.biddingStrategy === "ROAS Target"
      ) {
        updated.biddingStrategy = "" as BiddingStrategy
      }
    }

    onChange(updated)
  }

  const handlePlatformToggle = (platform: Platform) => {
    const current = data.platforms
    if (current.includes(platform)) {
      if (current.length <= 1) return
      handleChange(
        "platforms",
        current.filter(p => p !== platform),
      )
    } else {
      handleChange("platforms", [...current, platform])
    }
  }

  // Section completion badges
  const strategyComplete =
    !!data.campaignType &&
    !!data.optimizationGoal &&
    !!data.attributionModel &&
    !!data.attributionWindow
  const audienceComplete = !!data.adsetStrategy
  const targetingComplete =
    !!data.targetingGender && !!data.placements && data.platforms.length > 0
  const budgetComplete =
    !!data.budgetOptimization && !!data.biddingStrategy && data.budgetAmount > 0

  const isFormValid =
    strategyComplete &&
    audienceComplete &&
    targetingComplete &&
    budgetComplete &&
    !!data.targetingGeography

  const completedSections = [
    strategyComplete,
    audienceComplete,
    targetingComplete,
    budgetComplete,
  ].filter(Boolean).length

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* LEFT: Form */}
      <div className="lg:col-span-2 space-y-4">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-basic-white mb-1">
            Campaign Setup
          </h2>
          <p className="text-sm text-basic-light/60">
            Configure your Meta Ads campaign parameters
          </p>
        </div>

        {/* Section 1: Campaign Strategy */}
        <CollapsibleSection
          title="Campaign Strategy"
          subtitle="Objective, optimization, and attribution"
          isOpen={openSection === "strategy"}
          onToggle={() => toggleSection("strategy")}
          complete={strategyComplete}
          error={attempted && !strategyComplete}
        >
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Campaign Type"
              value={data.campaignType}
              options={CAMPAIGN_TYPES}
              onChange={v => handleChange("campaignType", v as CampaignType)}
              error={hasError("campaignType")}
            />
            <SelectField
              label="Optimization Goal"
              value={data.optimizationGoal}
              options={optimizationGoals}
              onChange={v =>
                handleChange("optimizationGoal", v as OptimizationGoal)
              }
              error={hasError("optimizationGoal")}
            />
            <SelectField
              label="Attribution Model"
              value={data.attributionModel}
              options={ATTRIBUTION_MODELS}
              onChange={v =>
                handleChange("attributionModel", v as AttributionModel)
              }
              error={hasError("attributionModel")}
              disabled={isEngagement}
            />
            <SelectField
              label="Attribution Window"
              value={data.attributionWindow}
              options={isEngagement ? ["1d Click"] : ATTRIBUTION_WINDOWS}
              onChange={v =>
                handleChange("attributionWindow", v as AttributionWindow)
              }
              error={hasError("attributionWindow")}
              disabled={isEngagement}
            />
          </div>
        </CollapsibleSection>

        {/* Section 2: Adset Strategy */}
        <CollapsibleSection
          title="Adset Strategy"
          subtitle="How to structure your adsets"
          isOpen={openSection === "audience"}
          onToggle={() => toggleSection("audience")}
          complete={audienceComplete}
          error={attempted && !audienceComplete}
        >
          <div className="grid grid-cols-3 gap-3">
            <SelectField
              label="Adset Strategy"
              value={data.adsetStrategy}
              options={ADSET_STRATEGIES}
              onChange={v => handleChange("adsetStrategy", v as AdsetStrategy)}
            />
          </div>
        </CollapsibleSection>

        {/* Section 3: Targeting Details */}
        <CollapsibleSection
          title="Targeting Details"
          subtitle="Demographics, placements, and platforms"
          isOpen={openSection === "targeting"}
          onToggle={() => toggleSection("targeting")}
          complete={targetingComplete}
          error={attempted && !targetingComplete}
        >
          <div className="space-y-4">
            {/* Gender icons */}
            <div>
              <label className="block text-xs font-medium text-basic-light/50 mb-2 uppercase tracking-wider">
                Gender
              </label>
              <div className="flex gap-2">
                {TARGETING_GENDERS.map(gender => {
                  const isSelected = data.targetingGender === gender
                  const Icon = gender === "All" ? UsersIcon : UserIcon
                  return (
                    <button
                      key={gender}
                      type="button"
                      onClick={() =>
                        handleChange(
                          "targetingGender",
                          gender as TargetingGender,
                        )
                      }
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                        isSelected
                          ? "bg-brand/20 text-brand border border-brand/40"
                          : "bg-system-gray-800/50 text-basic-light/50 border border-system-gray-700/30 hover:border-system-gray-600/50"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {gender}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Age range groups */}
            <div>
              <label className="block text-xs font-medium text-basic-light/50 mb-2 uppercase tracking-wider">
                Age Range
              </label>
              <div className="flex flex-wrap gap-1.5">
                {AGE_GROUPS.map(group => {
                  const isSelected = data.ageRanges.includes(group.label)
                  return (
                    <button
                      key={group.label}
                      type="button"
                      onClick={() => {
                        const newRanges = isSelected
                          ? data.ageRanges.filter(r => r !== group.label)
                          : [...data.ageRanges, group.label]
                        // Derive ageMin/ageMax from selected groups
                        const selectedGroups = AGE_GROUPS.filter(g =>
                          newRanges.includes(g.label),
                        )
                        onChange({
                          ...data,
                          ageRanges: newRanges,
                          ageMin:
                            selectedGroups.length > 0
                              ? Math.min(...selectedGroups.map(g => g.min))
                              : 0,
                          ageMax:
                            selectedGroups.length > 0
                              ? Math.max(...selectedGroups.map(g => g.max))
                              : 0,
                        })
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        isSelected
                          ? "bg-brand/20 text-brand border border-brand/40"
                          : "bg-system-gray-800/50 text-basic-light/50 border border-system-gray-700/30 hover:border-system-gray-600/50"
                      }`}
                    >
                      {group.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Placements */}
            <SelectField
              label="Placements"
              value={data.placements}
              options={PLACEMENTS_OPTIONS}
              onChange={v => handleChange("placements", v as Placements)}
            />

            {/* Platforms */}
            <div>
              <label className="block text-xs font-medium text-basic-light/50 mb-2 uppercase tracking-wider">
                Platforms
              </label>
              <div className="flex gap-2">
                {ALL_PLATFORMS.map(platform => {
                  const isSelected = data.platforms.includes(platform)
                  return (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => handlePlatformToggle(platform)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        isSelected
                          ? "bg-brand/20 text-brand border border-brand/40"
                          : "bg-system-gray-800/50 text-basic-light/50 border border-system-gray-700/30 hover:border-system-gray-600/50"
                      }`}
                    >
                      {platform}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Section 4: Budget & Bidding */}
        <CollapsibleSection
          title="Budget & Bidding"
          subtitle="Spend limits and bid strategy"
          isOpen={openSection === "budget"}
          onToggle={() => toggleSection("budget")}
          complete={budgetComplete}
          error={attempted && !budgetComplete}
        >
          <div className="grid grid-cols-3 gap-3">
            <SelectField
              label="Budget Optimization"
              value={data.budgetOptimization}
              options={BUDGET_OPTIMIZATIONS}
              onChange={v =>
                handleChange("budgetOptimization", v as BudgetOptimization)
              }
              error={hasError("budgetOptimization")}
            />
            <SelectField
              label="Bidding Strategy"
              value={data.biddingStrategy}
              options={biddingStrategies}
              onChange={v =>
                handleChange("biddingStrategy", v as BiddingStrategy)
              }
              error={hasError("biddingStrategy")}
            />
            <div>
              <label
                className={`block text-xs font-medium mb-1 uppercase tracking-wider ${
                  hasError("budgetAmount")
                    ? "text-error"
                    : "text-basic-light/50"
                }`}
              >
                Daily Budget
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-basic-light/40 text-sm">
                  $
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={data.budgetAmount || ""}
                  onChange={e =>
                    handleChange("budgetAmount", Number(e.target.value))
                  }
                  placeholder="0"
                  className={`input input-bordered input-sm w-full bg-system-gray-900/50 text-basic-white pl-7 pr-12 text-sm ${
                    hasError("budgetAmount") ? "border-error" : ""
                  }`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-basic-light/30 text-xs">
                  /day
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      {/* RIGHT: Map + Summary */}
      <div className="lg:col-span-3">
        <div className="sticky top-8 space-y-4">
          {/* World Map */}
          <div>
            <label
              className={`block text-xs font-medium mb-2 uppercase tracking-wider ${
                hasError("targetingGeography")
                  ? "text-error"
                  : "text-basic-light/50"
              }`}
            >
              Geography{" "}
              {attempted && !data.targetingGeography && (
                <span className="text-error">*</span>
              )}
            </label>
            <WorldMapSelector
              value={data.targetingGeography}
              onChange={v =>
                handleChange("targetingGeography", v as TargetingGeography)
              }
            />
          </div>

          {/* Campaign Title */}
          {campaignTitle && data.campaignType && (
            <div className="bg-system-gray-900/50 border border-system-gray-700/30 rounded-lg px-4 py-2.5">
              <p className="text-[10px] text-basic-light/40 uppercase tracking-widest mb-0.5">
                Campaign Title
              </p>
              <p className="text-xs text-basic-white font-mono break-all leading-relaxed">
                {campaignTitle}
              </p>
            </div>
          )}

          {/* Progress */}
          <div className="bg-system-gray-900/50 border border-system-gray-700/30 rounded-lg px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-basic-light/40 uppercase tracking-widest">
                Progress
              </p>
              <span className="text-xs text-brand font-medium">
                {completedSections}/4
              </span>
            </div>
            <div className="flex gap-1">
              {[
                strategyComplete,
                audienceComplete,
                targetingComplete,
                budgetComplete,
              ].map((done, i) => (
                <div
                  key={i}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    done ? "bg-brand" : "bg-system-gray-700/40"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Next button — spans full width, aligned right */}
      <div className="lg:col-span-5 flex justify-end pt-2">
        <button
          className="btn btn-primary btn-sm gap-1.5 px-6"
          onClick={() => {
            if (isFormValid) {
              onNext()
            } else {
              setAttempted(true)
            }
          }}
        >
          Next
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
